import React, { useEffect, useState } from "react";
import HotelIcon from "@mui/icons-material/Hotel";
import ChairIcon from "@mui/icons-material/Chair";
import { Button } from "@mui/material";
import RoomBedManager from "../pages/RoomBedManager";
import LibrarySeatManager from "../pages/LibrarySeatManager";
import SwitchRequestModal from "./SwitchRequestModal";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

// CSS animation added via useEffect
const pulseStyle = `
@keyframes pulse {
  0%   { transform: scale(1); }
  50%  { transform: scale(1.1); }
  100% { transform: scale(1); }
}
`;

const SeatMap = ({
  data = [],
  student,
  onSeatClick,
  isAdminView = false,
  refreshData,
  viewMode = "room",
}) => {
  const [open, setOpen] = useState(false);
  const [open1, setOpen1] = useState(false);
  const [switchModalOpen, setSwitchModalOpen] = useState(false);
  const [currentBookingForSwitch, setCurrentBookingForSwitch] = useState(null);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = pulseStyle;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Grouping logic
  const grouped = data.reduce((acc, item) => {
    const group =
      item.room?.room_number ||
      item.section?.section_name ||
      (item.bed_number ? "Hostel Beds" : "Study Centre Seats");

    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const getColor = (status = "", isBookedByCurrentUser = false) => {
    switch (status.toLowerCase()) {
      case "booked":
        return "#F44336";
      case "pending":
        return isBookedByCurrentUser ? "#FF9800" : "#4CAF50";
      default:
        return "#4CAF50";
    }
  };

  const handleSwitchClick = (item, e) => {
    e.stopPropagation(); // Prevent triggering the seat click
    setCurrentBookingForSwitch(item);
    setSwitchModalOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* 🔹 Legend */}
      <div className="flex justify-between">
        <div className="flex justify-end gap-6 text-sm font-medium text-gray-700">
          {[
            { label: "Available", color: "#4CAF50" },
            { label: "Pending", color: "#FF9800" },
            { label: "Booked", color: "#F44336" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: item.color }}
              ></div>
              {item.label}
            </div>
          ))}
        </div>
        {isAdminView && viewMode === "room" && (
          <div>
            <div className="bg-primaryColor text-white rounded shadow inline-block px-3">
              <Button
                onClick={() => setOpen(true)}
                className="!text-white !p-2"
              >
                + Add Room
              </Button>
            </div>
            <RoomBedManager
              open={open}
              onClose={() => setOpen(false)}
              onSuccess={() => {
                refreshData?.(); // <--- fetch fresh data
                setOpen(false); // close modal
              }}
            />
          </div>
        )}
        {isAdminView && viewMode === "seat" && (
          <div>
            <div className="bg-primaryColor text-white rounded shadow inline-block px-3">
              <Button
                onClick={() => setOpen1(true)}
                className="!text-white !p-2"
              >
                + Add Seat
              </Button>
            </div>
            <LibrarySeatManager
              open={open1}
              onClose={() => setOpen1(false)}
              onSuccess={() => {
                refreshData?.(); // <--- fetch fresh data
                setOpen1(false); // close modal
              }}
            />
          </div>
        )}
      </div>

      {/* 🔹 No Data */}
      {Object.keys(grouped).length === 0 ? (
        <div className="text-center text-gray-500 text-lg mt-10">
          🚫 No seats or beds available
        </div>
      ) : (
        <div
          className={`grid ${
            Object.keys(grouped).includes("Study Centre Seats") &&
            Object.keys(grouped).length === 1
              ? "grid-cols-1"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          } gap-4 sm:gap-6`}
        >
          {Object.entries(grouped).map(([group, items], index) => (
            <div
              key={index}
              className="border border-gray-300 rounded-md p-4 bg-white"
            >
              <h3 className="text-lg font-semibold mb-2">
                {group.match(/^[0-9]+$/) ||
                group.toLowerCase().startsWith("room") ||
                group.toLowerCase().startsWith("hostel")
                  ? `Room No - ${group}`
                  : group}
              </h3>
              <hr className="h-px bg-gray-200 border-0 mb-4" />

              <div
                className={`grid ${
                  Object.keys(grouped).includes("Study Centre Seats") &&
                  Object.keys(grouped).length === 1
                    ? "grid-cols-2 md:grid-cols-8"
                    : "grid-cols-2 sm:grid-cols-3"
                } gap-2 sm:gap-4`}
              >
                {items.map((item, idx) => {
                  const status = item.status?.toLowerCase();
                  const isPending = status === "pending";
                  const isAvailable = status === "available";
                  // Check if user already has a booking for this type
                  const hasHostelBooking =
                    student?.hostel_booking_id &&
                    student.hostel_booking_id !== item.booking_id;
                  const hasLibraryBooking =
                    student?.library_booking_id &&
                    student.library_booking_id !== item.booking_id;

                  // Check if this item is booked by current user
                  const isBookedByCurrentUser = item.bed_number
                    ? student?.hostel_booking_id &&
                      student.hostel_booking_id === item.booking_id
                    : student?.library_booking_id &&
                      student.library_booking_id === item.booking_id;

                  // Also prevent clicking on user's current booking
                  const isCurrentBooking = isBookedByCurrentUser;

                  // Prevent clicking if user already has a booking of the same type
                  const isBlockedByExistingBooking =
                    (item.bed_number && hasHostelBooking) || // Can't book hostel if already has hostel booking
                    (item.seat_number && hasLibraryBooking); // Can't book library if already has library booking

                  const isClickable = isAdminView
                    ? !!item.booking_id
                    : (isAvailable || isPending) &&
                      !isBlockedByExistingBooking &&
                      !isCurrentBooking;
                  return (
                    <div
                      key={idx}
                      onClick={() => isClickable && onSeatClick?.(item)}
                      className={`border rounded-xl w-full flex flex-col items-center shadow transition-transform duration-200 p-2 sm:p-3 relative group
                        ${
                          isBookedByCurrentUser
                            ? "bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-400 shadow-lg"
                            : "bg-white"
                        }
                        ${
                          isClickable
                            ? "cursor-pointer hover:scale-105 hover:shadow-lg"
                            : "cursor-not-allowed"
                        }
                      `}
                    >
                      {isBookedByCurrentUser && (
                        <>
                          <span
                            title="Your booking"
                            className="mr-1 absolute top-1 right-1 z-10 bg-blue-600 text-white rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                          >
                            YOU
                          </span>
                          {!isAdminView && (
                            <button
                              onClick={(e) => handleSwitchClick(item, e)}
                              title="Switch Room/Bed"
                              className="absolute bottom-1 right-1 z-10 bg-orange-500 hover:bg-orange-600 text-white rounded-full p-1 transition-colors duration-200"
                            >
                              <SwapHorizIcon sx={{ fontSize: 12 }} />
                            </button>
                          )}
                        </>
                      )}
                      {isPending &&
                        item.pending_count &&
                        !isBookedByCurrentUser && (
                          <span
                            title={`${item.pending_count} people applied for this.`}
                            className="mr-1 absolute top-1 right-1 z-10 bg-[#FF9800] text-white rounded-full px-2 text-center text-[14px]"
                          >
                            {item.pending_count}
                          </span>
                        )}
                      {/* Icon */}
                      <div className="flex relative flex-col items-center justify-center mt-2 sm:mt-4 mb-1 sm:mb-2">
                        {item.bed_number ? (
                          <HotelIcon
                            style={{
                              width: "32px",
                              height: "32px",
                              fill: getColor(status, isBookedByCurrentUser),
                            }}
                            className="sm:w-9 sm:h-9"
                          />
                        ) : (
                          <ChairIcon
                            style={{
                              width: "32px",
                              height: "32px",
                              fill: getColor(status, isBookedByCurrentUser),
                            }}
                            className="sm:w-9 sm:h-9"
                          />
                        )}
                        <div
                          className={`mt-1 sm:mt-2 text-sm sm:text-base font-semibold ${
                            isBookedByCurrentUser
                              ? "text-blue-700"
                              : "text-gray-800"
                          }`}
                        >
                          {item.bed_number || item.seat_number || "?"}
                        </div>
                      </div>
                      {/* Description */}
                      {item.description && (
                        <div
                          className="mt-1 text-xs text-gray-500 text-center w-full truncate"
                          title={item.description}
                        >
                          {item.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Switch Request Modal */}
      <SwitchRequestModal
        open={switchModalOpen}
        onClose={() => setSwitchModalOpen(false)}
        currentBooking={currentBookingForSwitch}
        bookingType={currentBookingForSwitch?.bed_number ? "hostel" : "library"}
        student={student}
        onSuccess={() => {
          refreshData?.();
          setSwitchModalOpen(false);
        }}
      />
    </div>
  );
};

export default SeatMap;
